import { Ty } from '../../ty';
import { implementsTy } from '../implements';

/**
 * Recursively checks if a type contains 'super' anywhere in its structure.
 */
const containsSuper = (ty: Ty.ExtendedAttributeType): boolean => {
  if (ty.ty.k === 'primitive' && ty.ty.t === 'super') {
    return true;
  }

  if (ty.ty.k === 'array') {
    return containsSuper(ty.ty.t);
  }

  if (ty.ty.k === 'struct') {
    return Object.values(ty.ty.fields).some((field) => containsSuper(field));
  }

  if (ty.ty.k === 'record') {
    return containsSuper(ty.ty.t);
  }

  return false;
};

export const ALLOWED_PRIMITIVE_CASTS: Record<
  Ty.PrimitiveAttributeType,
  readonly Ty.PrimitiveAttributeType[]
> = {
  boolean: ['boolean', 'string', 'int', 'super'],
  timestamp: ['timestamp', 'string', 'super'],
  day: ['timestamp', 'string', 'super'],
  month: ['timestamp', 'day', 'string', 'super'],
  year: ['timestamp', 'month', 'day', 'string', 'super'],
  float: ['float', 'int', 'string', 'super'],
  int: ['int', 'float', 'string', 'boolean', 'super'],
  string: [
    'string',
    'timestamp',
    'int',
    'float',
    'boolean',
    'super',
    'day',
    'month',
    'year',
  ],
  super: [
    'boolean',
    'timestamp',
    'float',
    'int',
    'string',
    'super',
    'day',
    'month',
    'year',
  ],
};

export const isAllowedCast = (params: {
  from: Ty.AttributeType;
  to: Ty.AttributeType;
}): boolean => {
  const { from, to } = params;

  // Allow casting anything to super (including nested types like ARRAY(super))
  const toExtended: Ty.ExtendedAttributeType = {
    ty: to,
    nullable: true,
    tags: [],
  };
  if (containsSuper(toExtended)) {
    return true;
  }

  const implementsTargetType = implementsTy({
    subject: from,
    req: to,
  });

  const isCastFromSuper = from.k === 'primitive' && from.t === 'super';

  const isCastFromWeirdTypeToString =
    (from.k === 'struct' || from.k === 'range' || from.k === 'array') &&
    to.k === 'primitive' &&
    to.t === 'string';

  const isValidPrimitiveToPrimitiveCast =
    to.k === 'primitive' &&
    ((from.k === 'primitive' &&
      ALLOWED_PRIMITIVE_CASTS[from.t].includes(to.t)) ||
      (from.k === 'enum' && ALLOWED_PRIMITIVE_CASTS.string.includes(to.t)));

  const isAllowedEnumToEnumCast =
    from.k === 'enum' &&
    to.k === 'enum' &&
    implementsTy({ subject: from, req: to });

  let isAllowedRangeCast = false;

  if (from.k === 'range') {
    if (to.k === 'range' && to.start <= from.start && to.end >= from.end) {
      isAllowedRangeCast = true;
    }

    if (to.k === 'primitive' && to.t === 'boolean') {
      isAllowedRangeCast = true;
    }
  }

  return (
    implementsTargetType ||
    isCastFromSuper ||
    isValidPrimitiveToPrimitiveCast ||
    isCastFromWeirdTypeToString ||
    isAllowedEnumToEnumCast ||
    isAllowedRangeCast
  );
};
